import { useState, useEffect } from 'react';
import { getAllFeatures, FEATURE_BASED_CHECKS } from '../data/modelSpecificChecks';
import {
  Settings2,
  ChevronDown,
  ChevronUp,
  Check,
  Plus,
  Sparkles,
} from 'lucide-react';

const FeatureSelector = ({ selectedFeatures, onFeaturesChange }) => {
  // Open by default until the user has picked features - selecting the
  // fuel type / features is what tailors the whole checklist.
  const [isExpanded, setIsExpanded] = useState(selectedFeatures.length === 0);
  const allFeatures = getAllFeatures();

  const toggleFeature = (featureId) => {
    if (selectedFeatures.includes(featureId)) {
      onFeaturesChange(selectedFeatures.filter(f => f !== featureId));
    } else {
      onFeaturesChange([...selectedFeatures, featureId]);
    }
  };

  const totalExtraChecks = selectedFeatures.reduce((acc, featureId) => {
    const feature = FEATURE_BASED_CHECKS[featureId];
    return acc + (feature?.items?.length || 0);
  }, 0);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
            <Settings2 className="w-5 h-5 text-purple-600" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-slate-800">Car Features</h3>
            <p className="text-xs text-slate-500">
              {selectedFeatures.length > 0 
                ? `${selectedFeatures.length} selected (${totalExtraChecks} extra checks)`
                : 'Add feature-specific checks'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {selectedFeatures.length > 0 && (
            <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
              {selectedFeatures.length}
            </span>
          )}
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 animate-slide-down">
          <p className="text-sm text-slate-600 mb-3">
            Select features your car has to add relevant inspection items:
          </p>
          
          <div className="grid grid-cols-2 gap-2">
            {allFeatures.map(feature => {
              const isSelected = selectedFeatures.includes(feature.id);
              return (
                <button
                  key={feature.id}
                  onClick={() => toggleFeature(feature.id)}
                  className={`p-3 rounded-xl border-2 text-left transition-all btn-touch ${
                    isSelected
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`font-medium text-sm ${
                        isSelected ? 'text-purple-800' : 'text-slate-800'
                      }`}>
                        {feature.name}
                      </p>
                      <p className={`text-xs mt-0.5 ${
                        isSelected ? 'text-purple-600' : 'text-slate-500'
                      }`}>
                        {feature.itemCount} checks
                      </p>
                    </div>
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                      isSelected 
                        ? 'bg-purple-500 text-white' 
                        : 'bg-slate-100 text-slate-400'
                    }`}>
                      {isSelected ? (
                        <Check className="w-3 h-3" />
                      ) : (
                        <Plus className="w-3 h-3" />
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {selectedFeatures.length > 0 && (
            <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-xl">
              <div className="flex items-center gap-2 text-purple-800">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {totalExtraChecks} additional checks added
                </span>
              </div>
              <p className="text-xs text-purple-600 mt-1">
                These will appear in relevant sections
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FeatureSelector;
